import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Calendar, ArrowRight, CheckCircle, Clock, Car, Cigarette, DollarSign, Brain } from 'lucide-react';
import CTA from '../../components/CTA';

const NewryQuitSmoking: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Affordable Quit Smoking Hypnotherapy Newry | Stop Smoking Near Border</title>
        <meta
          name="description"
          content="Affordable quit smoking hypnotherapy serving Newry from Belfast clinic (35 miles). Transparent pricing. Money-back guarantee. 60-80% success rates. Online sessions available."
        />
        <meta name="keywords" content="quit smoking Newry, stop smoking Newry, affordable hypnotherapy Newry, quit vaping Newry, smoking cessation near border" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/newry-quit-smoking/" />
        <meta property="og:title" content="Affordable Quit Smoking Hypnotherapy Newry | Stop Smoking Near Border" />
        <meta property="og:description" content="Affordable quit smoking hypnotherapy serving Newry. Transparent pricing. Money-back guarantee reduces risk." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Sharon Clare Hypnotherapy - Smoking Cessation Newry",
            "description": "CNHC registered hypnotherapy for smoking cessation, serving Newry from Belfast clinic. Affordable intensive 2-hour session with lifetime support and money-back guarantee. Transparent pricing for working-class demographic.",
            "url": "https://www.sharonclare-hypnotherapy.com/newry-quit-smoking/",
            "telephone": "07932139245",
            "email": "Sharon@sharonclare-hypnotherapy.com",
            "image": "https://www.sharonclare-hypnotherapy.com/images/sharon-clare.jpg",
            "serviceType": "Smoking Cessation Hypnotherapy",
            "areaServed": {
              "@type": "City",
              "name": "Newry",
              "containedInPlace": {
                "@type": "Country",
                "name": "Northern Ireland"
              }
            },
            "location": {
              "@type": "Place",
              "name": "Belfast Clinic (Ormeau Road)",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Belfast Wellness Clinic, 228 Ormeau Road",
                "addressLocality": "Belfast",
                "addressRegion": "County Antrim",
                "postalCode": "BT7 2FZ",
                "addressCountry": "GB"
              }
            },
            "sameAs": [
              "https://instagram.com/sharonclarehypnotherapy",
              "https://www.facebook.com/SharonClareHypnotherapy/",
              "https://www.linkedin.com/in/sharonclare-hypnotherapy"
            ]
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "How much does quit smoking hypnotherapy cost for Newry clients?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "The intensive 2-hour quit smoking session includes lifetime support and a money-back guarantee. For clients who prefer a gradual approach, a 3-session package is also available. I'm upfront about pricing because I understand cost matters for Newry families. Set the cost of one session against the thousands of pounds a 20-a-day habit costs you every year and you recoup it in just 3 weeks. The money-back guarantee removes financial risk."
                }
              },
              {
                "@type": "Question",
                "name": "Does the money-back guarantee work for Newry clients?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, absolutely. If you attend your intensive session, follow the program recommendations, and return within 3 months still smoking, you'll receive a full refund. No hidden conditions, no complicated requirements. This guarantee removes the financial risk entirely. For families watching their budget carefully, this makes trying hypnotherapy a risk-free decision. The only thing you're risking is staying trapped in smoking and continuing to spend thousands per year."
                }
              },
              {
                "@type": "Question",
                "name": "Is online quit smoking hypnotherapy as effective as in-person?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, online sessions are equally effective for smoking cessation. The hypnotherapy works the same way - accessing your subconscious to rewire smoking habits - whether you're sitting in my Belfast clinic or attending from your home in Newry. Many Newry clients prefer online sessions because it saves the 45-minute drive and eliminates travel costs. You get the same intensive 2-hour session, same lifetime support, same money-back guarantee, from the comfort of your own home."
                }
              },
              {
                "@type": "Question",
                "name": "How far in advance should I book for Newry?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Most clients can book within 1-2 weeks. Because the intensive quit smoking session is typically a single 2-hour appointment, scheduling is usually quite flexible. For Newry clients traveling to Belfast, I recommend booking for a time that works with your schedule - clinic days and times vary, and early evening appointments are popular with those who work. Online sessions offer even more flexibility as there's no travel time to consider."
                }
              },
              {
                "@type": "Question",
                "name": "Do you offer payment options for Newry families?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "The session cost is payable at the time of booking. While I don't offer formal payment plans, many clients find that once they decide to quit, they cover the cost within a few weeks simply by not buying cigarettes. Some clients also use the significant savings from not smoking in the first month to fund the 3-session package if they want additional support. The key is recognizing this as an investment that pays for itself within weeks."
                }
              },
              {
                "@type": "Question",
                "name": "Is pricing the same for Northern Ireland and Republic of Ireland clients?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, pricing is the same regardless of which side of the border you live on. The intensive session carries the same lifetime support and money-back guarantee for all clients. As a border city, Newry has strong connections with County Armagh and County Down on both sides. Online sessions make cross-border access particularly convenient, eliminating any travel considerations while providing the same professional smoking cessation support."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
              Affordable Quit Smoking Hypnotherapy for Newry
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-6 leading-relaxed">
              Transparent pricing with money-back guarantee - no hidden costs
            </p>
            <div className="bg-white rounded-xl p-6 mb-8 inline-block">
              <div className="flex items-center justify-center space-x-3">
                <Car className="w-6 h-6 text-accent-600" />
                <p className="text-lg text-gray-700">
                  <span className="font-semibold">35 miles</span> from Newry • <span className="font-semibold">45 min drive</span>
                </p>
              </div>
              <p className="text-gray-600 mt-2">Online sessions available • No travel costs</p>
            </div>
            <div className="bg-white rounded-xl p-6 mb-8 max-w-2xl mx-auto">
              <div className="flex items-start space-x-4">
                <DollarSign className="w-8 h-8 text-accent-600 flex-shrink-0 mt-1" />
                <div className="text-left">
                  <p className="text-lg font-semibold text-gray-900 mb-2">Investment That Pays for Itself</p>
                  <p className="text-gray-700">
                    <span className="font-semibold">One intensive session</span> vs. <span className="font-semibold">thousands of pounds a year on cigarettes</span>
                  </p>
                  <p className="text-accent-700 font-medium mt-1">Recoup investment in 3 weeks + money-back guarantee</p>
                </div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/book"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book Free Consultation
              </a>
              <a
                href="tel:07932139245"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Quit Smoking for Good in Newry
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                If you're living in Newry and ready to quit smoking or vaping, affordable professional support is available. I serve Newry clients from my Belfast clinic at 228 Ormeau Road in south Belfast, reached via the A1 and M1 north. The journey takes 40-45 minutes by car, and Newry is on the Belfast rail line if you would rather not drive, or you can choose online sessions from the comfort of your home - equally effective with no travel costs.
              </p>
              <p>
                Let's be honest about money, because I know it matters for Newry families. The intensive 2-hour quit smoking session includes lifetime support and a money-back guarantee. I'm upfront about pricing because there are no hidden costs, no ongoing fees, no pressure to buy expensive packages. The session is your total investment.
              </p>
              <p>
                Now compare that to what you're currently spending on smoking. A 20-a-day habit costs thousands of pounds a year - hundreds of pounds every single month going up in smoke. You recoup the cost of the quit smoking session in just 3 weeks of not smoking. By 2 months, you've saved enough to pay for groceries, bills, children's expenses, or emergency savings. By one year, a full year of cigarette money could transform your family's financial situation.
              </p>
              <p>
                The money-back guarantee removes all financial risk. If you attend your session, follow the program recommendations, and return within 3 months still smoking, you get a full refund. No complicated conditions, no hassle. This guarantee is possible because the majority of genuinely motivated clients succeed. When you're truly ready to quit, hypnotherapy gives you the tools to make it happen.
              </p>
              <p>
                Smoking isn't just draining your finances - it's affecting your health, your family, your quality of life. You've probably tried to quit before with willpower alone, or patches, or gum. But you're still smoking because these methods don't address the real problem: the subconscious habits and psychological patterns that control your smoking behavior. Solution-focused hypnotherapy rewires these patterns at their source, making it possible to become a non-smoker naturally - without cravings, without irritability, without feeling deprived.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ROI Section - Cost-Conscious Messaging */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              The Real Cost of Smoking for Newry Families
            </h2>
            <div className="bg-white rounded-2xl p-8 mb-8">
              <h3 className="text-xl font-heading font-semibold text-gray-900 mb-6 text-center">
                Let's Do the Maths
              </h3>
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-6 mb-6 text-center">
                <p className="text-sm text-gray-600 mb-2">Annual Smoking Cost (20/day)</p>
                <p className="text-2xl font-bold text-gray-900 mb-1">Thousands of pounds a year</p>
                <p className="text-sm text-gray-600">Enough every month to cover a weekly food shop</p>
              </div>
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <p className="text-lg font-semibold text-gray-900 mb-4 text-center">
                  Your Investment Pays Back:
                </p>
                <ul className="space-y-3 text-gray-700 max-w-2xl mx-auto">
                  <li className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-accent-600 mr-3 flex-shrink-0 mt-0.5" />
                    <span><strong>Week 3:</strong> Cost of the session fully recouped from not smoking</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-accent-600 mr-3 flex-shrink-0 mt-0.5" />
                    <span><strong>Month 2:</strong> Enough saved to cover a weekly food shop for a month</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-accent-600 mr-3 flex-shrink-0 mt-0.5" />
                    <span><strong>Month 6:</strong> Enough saved to pay off credit card debt or fund a family holiday</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-accent-600 mr-3 flex-shrink-0 mt-0.5" />
                    <span><strong>Year 1:</strong> A year of cigarette money could be an emergency fund, car deposit, house repairs, children's education</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-accent-600 mr-3 flex-shrink-0 mt-0.5" />
                    <span><strong>5 Years:</strong> A transformational amount for family financial security</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                For working-class Newry families, that monthly spend is a substantial amount. That's money that could go toward rent, groceries, utilities, children's expenses, paying down debt, or building an emergency fund. When you frame it this way, paying for one session to quit smoking isn't a cost - it's one of the smartest financial decisions you can make for your family's future.
              </p>
              <p>
                The money-back guarantee means there's literally zero financial risk. If it doesn't work, you get your money back. But if it does work (and research shows 60-80% success for motivated clients), you'll save thousands of pounds per year for the rest of your life. That's financial freedom you can feel.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              How the Intensive Session Works
            </h2>
            <div className="space-y-8">
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <div className="flex items-start space-x-4">
                  <Brain className="w-10 h-10 text-accent-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                      Why Willpower Alone Doesn't Work
                    </h3>
                    <p className="text-gray-700 mb-4">
                      You've probably tried to quit smoking with willpower before. Maybe you lasted a few hours, or a few days, or even a few weeks. But eventually you cracked - usually during a stressful moment, or after a few drinks, or when you just couldn't resist the craving anymore. This isn't a failure of your character or strength. It's because willpower alone can't override the subconscious habits that control your smoking.
                    </p>
                    <p className="text-gray-700">
                      Smoking is controlled by your basal ganglia - the autopilot part of your brain that runs habits without conscious thought. Every time you smoke in response to stress, boredom, after meals, or with coffee, you're strengthening a cue-routine-reward loop in your subconscious. Willpower (your prefrontal cortex) can fight this briefly, but it depletes throughout the day. By evening, when you're tired and stressed, the autopilot wins and you smoke. Hypnotherapy works differently: it rewires the subconscious patterns themselves, so you don't need willpower at all.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <div className="flex items-start space-x-4">
                  <CheckCircle className="w-10 h-10 text-accent-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                      The 2-Hour Intensive Session
                    </h3>
                    <p className="text-gray-700 mb-4">
                      The quit smoking program is delivered in one intensive 2-hour session (or 3-session package if you prefer gradual change). This session includes:
                    </p>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span><strong>Understanding your smoking triggers:</strong> We identify your specific cue-routine-reward patterns - stress smoking, social smoking, boredom smoking</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span><strong>Reframing your relationship with smoking:</strong> Shifting from "giving up" something you enjoy to "gaining freedom" from addiction</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span><strong>Hypnotherapy session:</strong> Deep relaxation while we work with your subconscious to break smoking habits and strengthen your identity as a non-smoker</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span><strong>Practical coping strategies:</strong> Tools for handling stress, cravings, and social situations without smoking</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-accent-600 mr-2 flex-shrink-0 mt-0.5" />
                        <span><strong>Lifetime support guarantee:</strong> If you ever feel tempted to smoke again, you can return for a free top-up session anytime</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              What Newry Clients Say
            </h2>
            <div className="space-y-6">
              <div className="bg-white rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "I was spending hundreds a month on cigarettes while struggling to pay bills. The cost seemed expensive until Sharon showed me the maths - I'd recoup it in 3 weeks. Four months smoke-free now and that money is going toward paying off debt. The money-back guarantee made trying it feel risk-free."
                </p>
                <p className="font-semibold text-gray-900">M.O., Newry - Financial motivation</p>
              </div>

              <div className="bg-white rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "I chose online sessions from home rather than driving to Belfast, which saved travel costs and time. Sharon was brilliant - same intensive session, same results, just more convenient. Six weeks smoke-free and I don't even think about cigarettes anymore. The savings are incredible."
                </p>
                <p className="font-semibold text-gray-900">T.D., Warrenpoint - Online session success</p>
              </div>

              <div className="bg-white rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "My kids kept asking me to quit but I didn't think I could afford hypnotherapy. When I actually did the maths on what I was spending on cigarettes every year, the session was obviously worth it. Three months smoke-free and I've already saved a serious amount. That money went to new school uniforms and a family day out."
                </p>
                <p className="font-semibold text-gray-900">L.K., Newry - Family motivation and ROI</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Frequently Asked Questions - Newry Quit Smoking
            </h2>
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How much does quit smoking hypnotherapy cost for Newry clients?
                </h3>
                <p className="text-gray-700">
                  The intensive 2-hour quit smoking session includes lifetime support and a money-back guarantee. For clients who prefer a gradual approach, a 3-session package is also available. I'm upfront about pricing because I understand cost matters for Newry families. Set the cost of one session against the thousands of pounds a 20-a-day habit costs you every year and you recoup it in just 3 weeks. The money-back guarantee removes financial risk.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Does the money-back guarantee work for Newry clients?
                </h3>
                <p className="text-gray-700">
                  Yes, absolutely. If you attend your intensive session, follow the program recommendations, and return within 3 months still smoking, you'll receive a full refund. No hidden conditions, no complicated requirements. This guarantee removes the financial risk entirely. For families watching their budget carefully, this makes trying hypnotherapy a risk-free decision. The only thing you're risking is staying trapped in smoking and continuing to spend thousands per year.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Is online quit smoking hypnotherapy as effective as in-person?
                </h3>
                <p className="text-gray-700">
                  Yes, online sessions are equally effective for smoking cessation. The hypnotherapy works the same way - accessing your subconscious to rewire smoking habits - whether you're sitting in my Belfast clinic or attending from your home in Newry. Many Newry clients prefer online sessions because it saves the 45-minute drive and eliminates travel costs. You get the same intensive 2-hour session, same lifetime support, same money-back guarantee, from the comfort of your own home.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How far in advance should I book for Newry?
                </h3>
                <p className="text-gray-700">
                  Most clients can book within 1-2 weeks. Because the intensive quit smoking session is typically a single 2-hour appointment, scheduling is usually quite flexible. For Newry clients traveling to Belfast, I recommend booking for a time that works with your schedule - clinic days and times vary, and early evening appointments are popular with those who work. Online sessions offer even more flexibility as there's no travel time to consider.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Do you offer payment options for Newry families?
                </h3>
                <p className="text-gray-700">
                  The session cost is payable at the time of booking. While I don't offer formal payment plans, many clients find that once they decide to quit, they cover the cost within a few weeks simply by not buying cigarettes. Some clients also use the significant savings from not smoking in the first month to fund the 3-session package if they want additional support. The key is recognizing this as an investment that pays for itself within weeks.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Is pricing the same for Northern Ireland and Republic of Ireland clients?
                </h3>
                <p className="text-gray-700">
                  Yes, pricing is the same regardless of which side of the border you live on. The intensive session carries the same lifetime support and money-back guarantee for all clients. As a border city, Newry has strong connections with County Armagh and County Down on both sides. Online sessions make cross-border access particularly convenient, eliminating any travel considerations while providing the same professional smoking cessation support.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTA />
    </main>
  );
};

export default NewryQuitSmoking;
